import React, { useEffect } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { ThemeProvider } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import { GlobalModalContextProvider } from "src/utils/GlobalModal/GlobalModalContext";
import { PermissionsContextProvider } from "src/utils/Permissions/PermissionsContext";
import { getLanguage } from "src/utils/Languages/LanguageContext";
import __ from "src/utils/Translations";
import SecureRoute from "src/utils/RoleBasedSecurity/SecurityComponents/SecureRoute";
import {
  absencesModuleRead,
  absencesAbsenceRequestsRead,
  absencesAbsencesConfigurationRead,
  absencesEmployeeAbsenceRecordsRead,
  absencesEmployeeAbsenceRecordsWrite,
  absencesOtherEmployeeAbsenceRequestsCreate,
  absencesAbsenceRequestsEdit,
  absencesAbsencesConfigurationWrite,
  absencesAbsenceRequestsImport,
} from "src/utils/RoleBasedSecurity/permissions";
import { theme as AbsencesTheme } from "src/theme/theme";
import HRToolsErrorBoundary from "src/Components/Error/ErrorBoundary";
import Page404 from "src/utils/ErrorPages/Page404/Page404";
import AbsencesMain from "./Main";
import AbsenceType from "./AbsenceType";
import AbsenceTypes from "./AbsenceType/AbsenceTypes";
import AbsenceGroups from "./AbsenceGroups";
import AbsenceGroupsEdit from "./AbsenceGroups/AbsenceGroupsEdit";
import Calendars from "./Calendars";
import AbsenceRecords from "./AbsenceRecords";
import AbsenceRecordsPattersAndCalendars from "./AbsenceRecords/AbsenceRecordsPatternsAndCalendars";
import AbsenceRecordsModifyBalance from "./AbsenceRecords/AbsenceRecordsModifyBalance";
import AbsenceRequestCreate from "./AbsenceRequest/AbsenceRequestCreate";
import AbsenceRequestEmployeeCreate from "./AbsenceRequest/AbsenceRequestEmployeeCreate";
import AbsenceRequestPreview from "./AbsenceRequest/AbsenceRequestPreview";
import AbsenceRequestEdit from "./AbsenceRequest/AbsenceRequestEdit";
import AbsenceRequests from "./AbsenceRequests";
import AbsenceRecordsAbsenceTypeHistory from "./AbsenceRecords/AbsenceRecordsAbsenceTypeHistory";
import AbsenceRecordsCalendarsImport from "./AbsenceRecords/AbsenceRecordsImports/AbsenceRecordsCalendarsImport";
import AbsenceRecordsPatternsImport from "./AbsenceRecords/AbsenceRecordsImports/AbsenceRecordsPatternsImport";
import AbsenceRecordsTypeRequests from "./AbsenceRecords/AbsenceRecordsTypeRequests";
import CalendarCreate from "./Calendars/Calendar/CalendarCreate";
import CalendarEdit from "./Calendars/Calendar/CalendarEdit";
import CalendarClone from "./Calendars/Calendar/CalendarClone";
import AbsencesConfiguration from "./AbsencesConfiguration";
import AbsenceRecordsEmployee from "./AbsenceRecords/AbsenceRecordsEmployee";
import AbsenceRequestsImport from "./AbsenceRecords/AbsenceRecordsImports/AbsenceRequestsImport";
import AbsenceTypePreview from "./AbsenceType/AbsenceTypePreview";

export default function Absences({ match }: RouteComponentProps) {
  const licenseKey = process.env?.REACT_APP_MUIX_LICENSE_KEY;

  if (
    typeof licenseKey === "string" &&
    typeof LicenseInfo.getLicenseKey() !== "string"
  ) {
    LicenseInfo.setLicenseKey(licenseKey);
  }

  const language = getLanguage();

  // needed to use pattern from @PageTitle.js to avoid persising title from previous route
  useEffect(() => {
    document.title = __("Nieobecności");
  }, []);

  return (
    <HRToolsErrorBoundary module="Absences">
      <ThemeProvider theme={AbsencesTheme({ language })}>
        <PermissionsContextProvider>
          <GlobalModalContextProvider>
            <Switch>
              <SecureRoute
                exact
                path={`${match.url}`}
                component={AbsencesMain}
                permission={absencesModuleRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-type/create`}
                component={AbsenceType}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-type/:absenceTypeId/edit`}
                component={AbsenceType}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-type/:absenceTypeId/preview`}
                component={AbsenceTypePreview}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-types`}
                component={AbsenceTypes}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-groups`}
                component={AbsenceGroups}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-groups/:groupId`}
                component={AbsenceGroupsEdit}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/calendars`}
                component={Calendars}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/calendars/create`}
                component={CalendarCreate}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/calendars/:calendarId/edit`}
                component={CalendarEdit}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/calendars/:calendarIdToClone/clone`}
                component={CalendarClone}
                permission={absencesAbsencesConfigurationRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-records`}
                component={AbsenceRecords}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/calendars-patterns/:employeeId`}
                component={AbsenceRecordsPattersAndCalendars}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/modify-balance/:employeeId/:absenceTypeId`}
                component={AbsenceRecordsModifyBalance}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/type-absence-requests/:employeeId/:absenceTypeId`}
                component={AbsenceRecordsTypeRequests}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-type-history/:employeeId/:absenceTypeId`}
                component={AbsenceRecordsAbsenceTypeHistory}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/employee/:employeeId`}
                component={AbsenceRecordsEmployee}
                permission={absencesEmployeeAbsenceRecordsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/import-calendars`}
                component={AbsenceRecordsCalendarsImport}
                permission={absencesEmployeeAbsenceRecordsWrite}
              />
              <SecureRoute
                exact
                path={`${match.url}/import-patterns`}
                component={AbsenceRecordsPatternsImport}
                permission={absencesEmployeeAbsenceRecordsWrite}
              />
              <SecureRoute
                exact
                path={`${match.url}/import-absence-requests`}
                component={AbsenceRequestsImport}
                permission={absencesAbsenceRequestsImport}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-requests`}
                component={AbsenceRequests}
                permission={absencesAbsenceRequestsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-request/create`}
                component={AbsenceRequestCreate}
                permission={absencesOtherEmployeeAbsenceRequestsCreate}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-request/create/:employeeId`}
                component={AbsenceRequestEmployeeCreate}
                permission={absencesOtherEmployeeAbsenceRequestsCreate}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-request/:absenceRequestId/preview`}
                component={AbsenceRequestPreview}
                permission={absencesAbsenceRequestsRead}
              />
              <SecureRoute
                exact
                path={`${match.url}/absence-request/:absenceRequestId/edit/:employeeId`}
                component={AbsenceRequestEdit}
                permission={absencesAbsenceRequestsEdit}
              />
              <SecureRoute
                exact
                path={`${match.url}/module-settings`}
                component={AbsencesConfiguration}
                permission={absencesAbsencesConfigurationWrite}
              />
              <Route>
                <Page404 />
              </Route>
            </Switch>
          </GlobalModalContextProvider>
        </PermissionsContextProvider>
      </ThemeProvider>
    </HRToolsErrorBoundary>
  );
}
